import {
  ReactElement,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { FAB } from '@beeline/design-system-react/extracted-styles';
import { Icons } from '@beeline/design-tokens/js/iconfont';
import { createCustomContext, usePostman } from '@beeline/lk-utils';

import styles from './styles.module.css';

import { useServicesApi } from ':api/common/useServicesApi';
import { TargetTemplate, TemplateFormWidget } from ':components';
import { ShowcaseContext } from ':constants';
import { serviceTrackEvent } from ':utils/analytics';

const DEFAULT_BOTTOM_POSITION = 24;
const TOP_POSITION = 120;

type Props = { children: ReactElement; serviceURLName?: string };
export function WithHDButtons({ children, serviceURLName }: Props): ReactNode {
  const [isOpen, setIsOpen] = useState(false);
  const postman = usePostman();
  const chatbotOpenFunc = useRef<null | (() => void)>(null);
  const [fabPosition, setFabPosition] = useState({
    bottom: DEFAULT_BOTTOM_POSITION,
  });

  const [targetTemplate, setTargetTemplate] = useState<TargetTemplate>({
    id: '',
    extra: null,
  });

  const { data: serviceData } = useServicesApi(serviceURLName);

  const showcaseInfo = useContext(ShowcaseContext);

  useEffect(() => {
    const handler = postman?.accept(
      `${serviceData?.component_id}/fabChatbotHandler`,
      ({ openWidget }: { openWidget: () => void }) => {
        chatbotOpenFunc.current = openWidget;
      },
    );

    return () => {
      handler?.remove();
    };
  }, [postman, serviceData?.component_id]);

  useEffect(() => {
    const handler = postman?.accept(
      `${serviceData?.component_id}/fabController`,
      ({
        position,
        marginBottom,
      }: {
        position: 'top' | 'bottom';
        marginBottom: number | undefined;
      }) => {
        const bottom = (() => {
          if (marginBottom !== undefined) return marginBottom;

          return position === 'bottom' ? DEFAULT_BOTTOM_POSITION : TOP_POSITION;
        })();

        setFabPosition({
          bottom,
        });
      },
    );

    return () => {
      handler?.remove();
      setFabPosition({ bottom: DEFAULT_BOTTOM_POSITION });
    };
  }, [postman, serviceData?.component_id]);

  const serviceHelpdesk = serviceData?.settings?.helpdesk;
  const showcaseHelpdesk = showcaseInfo?.settings.helpdesk;

  const getTemplateId = (): string => {
    const showcaseTemplateId = showcaseHelpdesk?.template_id ?? '';
    if (!serviceData || serviceHelpdesk?.use_showcase_template_id)
      return showcaseTemplateId;

    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    return serviceHelpdesk?.template_id || showcaseTemplateId;
  };

  const getShowConsultationButton = (): boolean => {
    if (!serviceData) return Boolean(showcaseHelpdesk?.is_consult_active);

    return Boolean(serviceHelpdesk?.is_consult_active);
  };

  const getShowChatbotButton = (): boolean => {
    if (!serviceData) return Boolean(showcaseHelpdesk?.is_chatbot_active);

    return Boolean(serviceHelpdesk?.is_chatbot_active);
  };

  const handleOpen = (
    id?: TargetTemplate['id'],
    extra?: TargetTemplate['extra'],
  ): void => {
    // do not use ?? here, it'll pass id with empty string value
    setTargetTemplate({ id: id ? id : getTemplateId(), extra });
  };

  createCustomContext({
    helpdesk: {
      handleHelpdeskWidgetOpen: handleOpen,
    },
  });

  const templateId = getTemplateId();
  const showConsultationButton = getShowConsultationButton();
  const showChatbotButton = getShowChatbotButton();

  const buttonsList = (
    <div className={styles.fabContainer}>
      {showChatbotButton && (
        <FAB
          iconName={Icons.Message}
          onClick={() => {
            chatbotOpenFunc.current?.();
            setIsOpen(false);
          }}
          type='extended'
        >
          Чат-бот
        </FAB>
      )}
      {showConsultationButton && (
        <FAB
          iconName={Icons.Lifebelt}
          onClick={() => {
            handleOpen(templateId, {
              'Причина обращения': 'Консультация',
            });
            setIsOpen(false);
            serviceTrackEvent(
              'helpdesk_request',
              'request_button_consultation_click',
            );
          }}
          type='extended'
        >
          Нужна консультация
        </FAB>
      )}
      <FAB
        iconName={Icons.WarningTriangle}
        onClick={() => {
          handleOpen(templateId, null);
          setIsOpen(false);
          serviceTrackEvent('helpdesk_request', 'request_button_error_click');
        }}
        type='extended'
      >
        Ошибка на странице?
      </FAB>
      <FAB
        iconName={Icons.Close}
        onClick={() => {
          setIsOpen(false);
        }}
        type='standard'
      />
    </div>
  );

  return (
    <>
      {children}
      <div className={styles.mainBlock} style={{ bottom: fabPosition.bottom }}>
        {isOpen ? (
          buttonsList
        ) : (
          <FAB
            iconName={Icons.QuestionCircled}
            onClick={() => {
              serviceTrackEvent('helpdesk_request', 'fab_button_click');
              setIsOpen(true);
            }}
            type='standard'
          />
        )}
      </div>
      <TemplateFormWidget
        consumerName={serviceURLName ?? 'LKE'}
        targetTemplate={targetTemplate}
      />
    </>
  );
}
